import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Button, Icons, Modal, SpinnerOverlay } from '@components/common';
import ProfileTabSection from '@components/common/ProfileTabSection';
import TechnologyForm, { TTechnologyFormSubmitHandler } from '@components/ModalComponents/TechnologyForm/TechnologyForm';
import CellsTab from '@components/routes/AdminPanel/SiteProfile/CellsTab';

import { useSiteProfile } from '@hooks/useSiteProfile';

import { auth } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { TTechnologyNameTitle } from '@interfaces/common/common';

import { ETechnologiesValues, EVendorsValues, TTechnologiesValues } from '@enums/common';

import colors from '@constants/colors';
import { ALL_TECHNOLOGIES_QUANTITY, technologyTabs } from '../constants';

import styles from './NokiaProfile.module.scss';

// NOTE: component has been moved from SiteProfile to NokiaProfile, so it's the same as it was in SiteProfile before
const NokiaProfile = () => {
	const { id } = useParams();
	const { accessPermission: { networkOperatorTelecomSitesAttributeUpdater } } = useAppSelector(auth);
	const [isTechnologyFormModal, setIsTechnologyFormModal] = useState(false);
	const [isDirtyTechnologyFormModal, setIsDirtyTechnologyFormModal] = useState(false);
	const [editableTechnology, setEditableTechnology] = useState<TTechnologyNameTitle & { node: string } | null>(null);
	const [nodeName, setNodeName] = useState<string>();

	const toggleTechnologyModal = () => setIsTechnologyFormModal(prevState => !prevState);

	const openCreateTechnologyModal = () => {
		setEditableTechnology(null);
		toggleTechnologyModal();
	};

	const openEditTechnologyModal = (technology: TTechnologyNameTitle & { node: string } | null) => {
		setEditableTechnology(technology);
		toggleTechnologyModal();
	};

	const {
		profile,
		isLoadingProfile,
		createTechnology,
		editTechnology,
		isLoadingTechnologyForm,
		deleteTechnology,
	} = useSiteProfile({
		id,
		closeModals: toggleTechnologyModal,
	});

	const isNokia = profile?.vendor?.name !== EVendorsValues.limenet;
	const isEditAccess = networkOperatorTelecomSitesAttributeUpdater && isNokia;

	const deleteTechnologyHandler = (technologyValue: TTechnologiesValues) =>
		deleteTechnology({ id: profile.id, technologyValue });

	const existingTechnologies = profile.nokiaSite?.technologies?.map<TTechnologiesValues>(({ name }) => name);
	const getTechnology = (value: TTechnologiesValues) =>
		profile.nokiaSite?.technologies?.find(({ name }) => name === value);

	const [currentTab, setCurrentTab] = useState('');

	const tabs = profile.nokiaSite?.technologies?.length
		? technologyTabs.filter(tab => existingTechnologies?.includes(tab.value as TTechnologiesValues)) : [];

	useEffect(() => {
		if (profile.nokiaSite?.technologies?.length) setCurrentTab(profile.nokiaSite?.technologies[0].name);
		else setCurrentTab('');
	}, [profile.nokiaSite?.technologies]);

	useEffect(() => {
		setNodeName(currentTab === ETechnologiesValues.T_5G ? 'gNodeB ID' : 'eNodeB ID');
	}, [currentTab]);

	const getCurrentTab = () => {
		let technology;
		switch (currentTab) {
			case ETechnologiesValues.T_4G_TDD:
				technology = {
					...getTechnology(ETechnologiesValues.T_4G_TDD),
					node: profile.nokiaSite?.technology4gTdd?.enodeBId,
				};
				break;
			case ETechnologiesValues.T_4G_FDD:
				technology = {
					...getTechnology(ETechnologiesValues.T_4G_FDD),
					node: profile.nokiaSite?.technology4gFdd?.enodeBId,
				};
				break;
			case ETechnologiesValues.T_5G:
				technology = {
					...getTechnology(ETechnologiesValues.T_5G),
					node: profile.nokiaSite?.technology5g?.gnodeBId,
				};
				break;
			default:
				technology = null;
		}

		if (!technology) return null;

		return (
			<CellsTab
				deleteTechnology={deleteTechnologyHandler}
				id={profile.id}
				isEditAccess={isEditAccess}
				nodeName={nodeName}
				setEditableTechnology={openEditTechnologyModal}
				technology={technology as TTechnologyNameTitle & { node: string }}
			/>
		);
	};

	const submitHandler = (
		editableTechnology ? editTechnology : createTechnology
	) as unknown as TTechnologyFormSubmitHandler;

	return (
		<div className={styles.nokiaProfileContainer}>
			{isLoadingProfile && <SpinnerOverlay />}
			<Modal
				isConfirmDialogNeededOnClose
				isDirtyForm={isDirtyTechnologyFormModal}
				isOpen={isTechnologyFormModal}
				onClose={toggleTechnologyModal}
				title={editableTechnology ? `Edit ${nodeName}` : 'Add network technology'}
				width='400px'
			>
				<TechnologyForm
					editableTechnology={editableTechnology}
					existingTechnologies={existingTechnologies}
					isEditMode={!!editableTechnology}
					isLoadingForm={isLoadingTechnologyForm}
					setIsDirtyFormModal={setIsDirtyTechnologyFormModal}
					siteProfile={profile}
					submitHandler={submitHandler}
					toggleTechnologyModal={toggleTechnologyModal}
				/>
			</Modal>

			<ProfileTabSection
				currentTab={currentTab}
				getCurrentTab={getCurrentTab}
				maxWidth='none'
				setCurrentTab={setCurrentTab}
				tabWidth='170px'
				tabs={tabs}
			>
				{existingTechnologies?.length !== ALL_TECHNOLOGIES_QUANTITY
					&& <div className={styles.addButton}>
						<Button
							disabled={!isEditAccess}
							icon={Icons.plus({ color: isEditAccess ? colors.indigo70 : colors.gray40 })}
							onClick={openCreateTechnologyModal}
							text='Add network technology'
							withoutBackground
						/>
					</div>
				}
			</ProfileTabSection>
		</div>
	);
};

export default NokiaProfile;
